import { useState } from "react";
import { Plus } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import {
  MENS_SERVICES,
  WOMENS_SERVICES,
  ADDONS,
} from "../data/services";

const VAGARO_URL = "https://www.vagaro.com/axtreme/services";
const RED = "#CC2222";
const CHARCOAL = "#1A1A1A";

function ServiceCard({ s, isOpen, onToggle, t }) {
  return (
    <div
      className="bg-white border border-[#E0E0E0] transition-shadow duration-300 hover:shadow-md"
      style={{ borderBottom: `2px solid ${RED}` }}
      data-testid={`service-row-${s.slug}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 sm:px-8 py-6 text-left"
        aria-expanded={isOpen}
      >
        <span
          className="text-2xl sm:text-3xl"
          style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 700, color: CHARCOAL, letterSpacing: "-0.01em" }}
        >
          {s.name}
        </span>
        <div className="flex items-center gap-6">
          <span className="text-xl sm:text-2xl" style={{ color: RED, fontWeight: 600 }}>
            {s.price}
          </span>
          <Plus
            className="w-5 h-5 transition-transform duration-300"
            strokeWidth={1.75}
            style={{ transform: isOpen ? "rotate(45deg)" : "rotate(0deg)", color: CHARCOAL }}
          />
        </div>
      </button>
      <div
        className="grid transition-all duration-300 ease-out overflow-hidden"
        style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div className="px-6 sm:px-8 pb-7 pt-1" data-testid={`service-expanded-${s.slug}`}>
            <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-start">
              <img
                src={s.image}
                alt={s.name}
                className="object-cover flex-shrink-0"
                style={{ width: 120, height: 120 }}
                loading="lazy"
              />
              <p className="text-base leading-relaxed flex-1" style={{ color: "#2C2825" }}>
                {s.description}
              </p>
            </div>
            <a
              href={VAGARO_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-block px-7 py-3 text-[11px] tracking-[0.3em] uppercase text-white bg-[#CC2222] hover:bg-[#a81b1b] transition-colors"
              data-testid={`service-book-${s.slug}`}
            >
              Book Now
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ServiceList({ category, title, intro }) {
  const t = useTheme();
  const [open, setOpen] = useState(null);
  const items = category === "mens" ? MENS_SERVICES : WOMENS_SERVICES;

  return (
    <div data-testid={`service-list-${category}`} style={{ background: "#FFFFFF" }}>
      <section className="max-w-5xl mx-auto px-6 lg:px-8 pt-20 pb-12">
        <div className="flex items-center gap-4 mb-3">
          <span className="h-px w-12" style={{ background: RED }} />
          <div className="text-[11px] tracking-[0.4em] uppercase font-semibold" style={{ color: RED }}>
            {category === "mens" ? "Men's Studio" : "Women's Hair"}
          </div>
        </div>
        <h1 className="text-5xl sm:text-6xl" style={{ color: CHARCOAL, fontFamily: "'Archivo', sans-serif", fontWeight: 800 }}>{title}</h1>
        <p className="mt-4 max-w-2xl" style={{ color: "#2C2825" }}>{intro}</p>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-10">
        <div className="grid gap-4">
          {items.map((s) => (
            <ServiceCard
              key={s.slug}
              s={s}
              isOpen={open === s.slug}
              onToggle={() => setOpen(open === s.slug ? null : s.slug)}
              t={t}
            />
          ))}
        </div>
      </section>

      {/* Special offer banner */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-10" data-testid="fiu-offer-banner">
        <div
          className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4 px-6 py-5 text-white"
          style={{ background: RED }}
        >
          <div className="flex items-baseline gap-3">
            <span className="text-[10px] tracking-[0.3em] uppercase font-semibold opacity-90">Special Offer</span>
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="text-base sm:text-lg" style={{ fontFamily: "'Archivo', sans-serif", fontWeight: 700, letterSpacing: "0.01em" }}>
              FIU Students — 20% OFF on Haircuts
            </div>
            <div className="text-[11px] tracking-[0.1em] opacity-85 mt-0.5">
              Must show valid FIU Student ID
            </div>
          </div>
        </div>
      </section>

      {/* Add-Ons compact list */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-24" data-testid="addons-section">
        <div className="flex items-center gap-3 mb-4">
          <span className="h-px w-10" style={{ background: RED }} />
          <h3 className="text-[10px] tracking-[0.4em] uppercase font-semibold" style={{ color: RED }}>Add-Ons</h3>
        </div>
        <ul className="border-t border-[#E0E0E0]">
          {ADDONS.map((a) => (
            <li
              key={a.slug}
              className="flex items-center justify-between py-2.5 border-b border-[#E0E0E0]"
              data-testid={`addon-${a.slug}`}
            >
              <span className="text-[14px]" style={{ color: CHARCOAL }}>{a.name}</span>
              <span className="text-[14px]" style={{ color: RED, fontWeight: 600 }}>{a.price}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
